import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AuthLayout } from "../components/AuthLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SocialAuthButtons } from "../components/SocialAuthButtons";
import { errText } from "../lib/api";
import { resolveRole, portalPath } from "../lib/roles";
import { supabase } from "../lib/supabaseClient";

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { data, error } = await signIn({ email, password });
    if (error) {
      setLoading(false);
      const m = errText(error, "Sign in failed. Please check your details.");
      setError(m);
      toast.error(m);
      return;
    }
    const role = await resolveRole(data?.user);
    if (role === "gp" && data?.user) {
      const loginCount = Number(data.user.user_metadata?.login_count || 0) + 1;
      await supabase.auth.updateUser({ data: { ...data.user.user_metadata, login_count: loginCount } });
    }
    setLoading(false);
    toast.success("Welcome back!");
    navigate(portalPath(role), { replace: true });
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in — GPs, clinicians and clinic managers all use this page." testId="login-page">
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@clinic.com"
            data-testid="login-email-input"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-sm font-medium text-primary hover:underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="login-password-input"
          />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Sign in
        </Button>
      </form>
      <div className="mt-6">
        <SocialAuthButtons />
      </div>
      <p className="mt-6 text-center text-sm text-slate-500">
        Don't have an account?{" "}
        <Link to="/signup" className="font-medium text-primary hover:underline" data-testid="go-to-signup-link">
          Create one
        </Link>
      </p>
    </AuthLayout>
  );
}
